import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import DocumentTakeover from "../design/DocumentTakeover";
import BatchSignPage from "./BatchSignPage";
import CodeStep from "./CodeStep";
import * as api from "./signApi";

type Phase = "loading" | "code" | "review" | "done" | "closed" | "error" | "batch";

/** The API refuses a longer reason (DeclineIn in app/authorised_requests/signatures/schemas.py). */
export const DECLINE_REASON_MAX = 1000;

/**
 * The public signer page, /app/sign#t=<token>, rendered OUTSIDE the sign-in gate (app/App.tsx). A Contract
 * Sign link asks for the emailed code before the document is shown; an Approval link shows it at once.
 * The document, its title and the stage rail are the shared DocumentTakeover (Figma 20); this page supplies
 * only the code step and the sign / approve controls. A new screen: it still needs a design pass
 * (Desrae / Trinity) before it is called finished.
 */
// Shown while the server withholds the title (a Contract Sign link before its code is verified).
const GENERIC_TITLE = "Document to sign";
// Shown once the link is locked after too many wrong codes: on the 423 LINK_LOCKED refusal and when a reopened
// link's view reports locked (the view carries no message), so both read the same.
const LINK_LOCKED_MESSAGE = "Too many wrong codes. Ask the sender to send a new link.";

const SignPage: React.FC = () => {
  // Read the fragment ONCE, then remove it from the address bar and history so the link token is not left
  // in view (shoulder-surfing, screenshots) or in the session history.
  const [token] = useState(() => api.tokenFromHash());
  const [info, setInfo] = useState<api.SignView | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [accept, setAccept] = useState(false);
  const [comment, setComment] = useState("");
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneText, setDoneText] = useState("");

  // Every view read takes a number; a result that is no longer the newest read is dropped, so a slow
  // earlier response can never overwrite a later one.
  const viewSeq = useRef(0);

  const fail = (e: any) => {
    // The link is locked after too many wrong codes (a 423 LINK_LOCKED, or a reopened link's view). Only the
    // sender can re-issue it, so end on the locked message with no code controls (the same page as any other
    // error) and drop the session. Any view read still in flight is retired, so a slower one can never
    // overwrite this terminal state.
    if (e?.code === "LINK_LOCKED") {
      viewSeq.current++;
      api.clearSession();
      setError(LINK_LOCKED_MESSAGE);
      setPhase("error");
      return;
    }
    setError(e?.message ?? "Something went wrong.");
  };

  // The server refuses a Contract Sign call once the code session has lapsed (30 minutes, or a lost session).
  // Return to the code step instead of stranding the signer; what they typed is kept.
  const onSignError = (e: any) => {
    if (e?.code === "OTP_REQUIRED" && info?.kind === "contract_sign") {
      api.clearSession();
      setCodeSent(false);
      setCode("");
      setFile(null);
      setPhase("code");
      setError("For your security your code has timed out. Email yourself a new code to carry on.");
      return;
    }
    fail(e);
  };

  const openDocument = async (t: string, v: api.SignView, seq: number) => {
    const f = await api.pdfFile(t, v.title);
    if (seq !== viewSeq.current) return;
    setFile(f);
    setPhase("review");
  };

  // The code is now verified: read the view again so the title and signer name the server withheld appear.
  const afterVerify = async (t: string) => {
    const seq = ++viewSeq.current;
    const v = await api.view(t);
    if (seq !== viewSeq.current) return;
    setInfo(v);
    await openDocument(t, v, seq);
  };

  const sendCode = async (t: string) => {
    setError(null);
    setBusy(true);
    try {
      await api.sendOtp(t);
      setCodeSent(true);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const continueWithCode = async (t: string) => {
    setError(null);
    setBusy(true);
    try {
      await api.verifyOtp(t, code);
      await afterVerify(t);
    } catch (e) {
      onSignError(e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (window.location.hash) {
      // Keep history.state (React Router's). The router's own location.hash is deliberately left stale:
      // nothing reads it, and the token is only ever held in this component's state.
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setError("For your security the link is removed from the address bar once opened. Open it again from your email.");
      setPhase("error");
      return;
    }
    const seq = ++viewSeq.current;
    api
      .view(token)
      .then((v) => {
        if (seq !== viewSeq.current) return;
        // A reopened locked link: straight to the locked terminal state, never the code step (no /otp call).
        // A missing field (an older backend) is not locked.
        if (v.locked === true) {
          fail({ code: "LINK_LOCKED" });
          return;
        }
        setInfo(v);
        if (v.needs_otp && !v.verified) setPhase("code");
        else return openDocument(token, v, seq);
      })
      .catch((e) => {
        if (seq !== viewSeq.current) return;
        // One link for several documents (version-43): the batch page takes over with the same token.
        if (api.isBatchLink(e)) {
          setPhase("batch");
          return;
        }
        fail(e);
        setPhase("error");
      });
  }, [token]);

  const act = async (fn: () => Promise<void>, text: string) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      setDoneText(text);
      setPhase("done");
    } catch (e) {
      onSignError(e);
    } finally {
      setBusy(false);
    }
  };

  if (token && phase === "batch") return <BatchSignPage token={token} />;
  if (!token || phase === "error") {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (phase === "loading" || !info) {
    return (
      <Box p={4}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }
  if (phase === "done" || phase === "closed") {
    return (
      <Box p={4} maxWidth={720} mx="auto">
        <Typography variant="h5" component="h1" gutterBottom>
          {info.title || GENERIC_TITLE}
        </Typography>
        <Alert severity={phase === "done" ? "success" : "info"}>
          {phase === "done" ? doneText : "Nothing was signed. You can close this tab; the link still works."}
        </Alert>
      </Box>
    );
  }
  if (phase === "code") {
    return (
      <CodeStep
        title={info.title || GENERIC_TITLE}
        signerName={info.signer_name}
        error={error}
        busy={busy}
        codeSent={codeSent}
        code={code}
        onCodeChange={setCode}
        onSend={() => sendCode(token)}
        onContinue={() => continueWithCode(token)}
      />
    );
  }

  const signing = info.kind === "contract_sign";
  const stages = signing
    ? [
        { key: "code", label: "Code" },
        { key: "review", label: "Review" },
        { key: "sign", label: "Sign" },
      ]
    : [
        { key: "review", label: "Review" },
        { key: "approve", label: "Approve" },
      ];
  // Declining asks why first: the reason is required, and the sender reads it.
  const declineButton = (
    <Button disabled={busy} onClick={() => setDeclining(true)}>
      Decline
    </Button>
  );
  return (
    <DocumentTakeover
      file={file}
      fileName={file?.name ?? null}
      title={info.title || GENERIC_TITLE}
      backLabel="Close"
      enableSaveClose={false}
      onBack={() => setPhase("closed")}
      busy={busy}
      rail={{
        stages,
        stage: signing ? 1 : 0,
        phase: "running",
        title: signing ? "Review and sign" : "Review and approve",
        note: info.signer_name ? `For ${info.signer_name}` : "",
      }}
      notices={error ? [{ key: "error", severity: "error", text: error, onDismiss: () => setError(null) }] : []}
    >
      <Stack spacing={2} p={2}>
        {declining ? (
          <>
            <TextField
              label="Reason for declining"
              required
              autoFocus
              multiline
              minRows={3}
              value={reason}
              inputProps={{ maxLength: DECLINE_REASON_MAX }}
              helperText={`The sender will see this. ${reason.length}/${DECLINE_REASON_MAX}`}
              onChange={(e) => setReason(e.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="error"
                disabled={busy || !reason.trim()}
                onClick={() => act(() => api.decline(token, reason.trim()), "Declined. The sender has been told why.")}
              >
                Confirm decline
              </Button>
              <Button disabled={busy} onClick={() => setDeclining(false)}>
                Cancel
              </Button>
            </Stack>
          </>
        ) : signing ? (
          <>
            <TextField label="Your full name" value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Your title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={accept} onChange={(e) => setAccept(e.target.checked)} />}
              label="I accept this document and agree to sign it electronically"
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={busy || !accept || !name.trim()}
                onClick={() =>
                  act(
                    () => api.sign(token, { typed_name: name.trim(), typed_title: title.trim(), accept }),
                    "You have signed. The executed copy will be emailed to you once every party has signed."
                  )
                }
              >
                Sign
              </Button>
              {declineButton}
            </Stack>
          </>
        ) : (
          <>
            <TextField
              label="Comment (optional)"
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => act(() => api.approve(token, comment), "Approved. Thank you.")}
              >
                Approve
              </Button>
              {declineButton}
            </Stack>
          </>
        )}
      </Stack>
    </DocumentTakeover>
  );
};

export default SignPage;
